import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Mail, Lock, Building2, AlertCircle } from 'lucide-react';
import { AuthService } from '../handling_data/services/AuthService';

export const Login: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [credentials, setCredentials] = useState({
        email: '',
        password: ''
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCredentials(prev => ({
            ...prev,
            [e.target.type === 'email' ? 'email' : 'password']: e.target.value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await AuthService.login(credentials);
            if (response.status === 'success') {
                navigate('/');
            } else {
                setError(response.message || 'فشل تسجيل الدخول');
            }
        } catch (err: any) {
            if (err.response) {
                const status = err.response.status;
                if (status === 422) {
                    setError('يرجى التأكد من صحة البيانات المدخلة');
                } else if (status === 401) {
                    setError('بيانات الاعتماد غير صحيحة');
                } else {
                    setError('حدث خطأ في السيرفر، يرجى المحاولة لاحقاً');
                }
            } else {
                setError('تعذر الاتصال بالسيرفر');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="h-screen w-screen flex items-center justify-center relative overflow-hidden bg-black" dir="rtl">
            {/* Full Screen Background with Overlay */}
            <div className="absolute inset-0 z-0">
                <div className="absolute inset-0 bg-black/50 z-10"></div>
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?q=80&w=2670&auto=format&fit=crop')] bg-cover bg-center"></div>
            </div>

            {/* Glassmorphism Card */}
            <div className="relative z-20 w-full max-w-md p-4 mx-4">
                <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl shadow-2xl p-6 lg:p-8 text-center ring-1 ring-white/10">

                    {/* Logo & Header */}
                    <div className="mb-6">
                        <div className="w-16 h-16 bg-primary/80 backdrop-blur-md rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg border border-white/10 rotate-3 hover:rotate-6 transition-transform duration-300">
                            <Building2 className="w-8 h-8 text-white" />
                        </div>
                        <h1 className="text-2xl lg:text-3xl font-serif font-bold text-white mb-1 drop-shadow-md">فيلا إسطنبول</h1>
                        <p className="text-gray-200 font-sans text-xs lg:text-sm">لوحة التحكم الإدارية</p>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-xl flex items-center gap-2 text-red-200 text-xs text-right animate-shake">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-4 text-right">
                        <div className="space-y-3">
                            <div className="relative group">
                                <Input
                                    type="email"
                                    value={credentials.email}
                                    onChange={handleChange}
                                    placeholder="البريد الإلكتروني"
                                    className="pr-10 py-2.5 bg-white/10 border-white/10 text-white placeholder:text-gray-300 focus:bg-white/20 focus:border-accent/50 focus:ring-accent/30 transition-all rounded-xl backdrop-blur-sm text-sm"
                                    required
                                />
                                <Mail className="w-4 h-4 text-gray-300 absolute right-3.5 top-3 group-focus-within:text-accent transition-colors" />
                            </div>

                            <div className="relative group">
                                <Input
                                    type="password"
                                    value={credentials.password}
                                    onChange={handleChange}
                                    placeholder="كلمة المرور"
                                    className="pr-10 py-2.5 bg-white/10 border-white/10 text-white placeholder:text-gray-300 focus:bg-white/20 focus:border-accent/50 focus:ring-accent/30 transition-all rounded-xl backdrop-blur-sm text-sm"
                                    required
                                />
                                <Lock className="w-4 h-4 text-gray-300 absolute right-3.5 top-3 group-focus-within:text-accent transition-colors" />
                            </div>
                        </div>

                        <div className="flex items-center justify-between text-xs">
                            <label className="flex items-center gap-2 cursor-pointer group">
                                <input type="checkbox" className="rounded border-white/30 bg-white/10 text-accent focus:ring-accent w-3 h-3" />
                                <span className="text-gray-200 group-hover:text-white transition-colors">تذكرني</span>
                            </label>
                            <button type="button" className="text-accent hover:text-white font-medium transition-colors bg-transparent border-none p-0 cursor-pointer">
                                نسيت كلمة المرور؟
                            </button>
                        </div>

                        <Button
                            type="submit"
                            className="w-full py-3 text-base bg-accent hover:bg-accent/90 text-white shadow-lg hover:shadow-accent/40 transition-all rounded-xl font-bold"
                            isLoading={loading}
                        >
                            تسجيل الدخول
                        </Button>
                    </form>

                    <div className="mt-6 pt-4 border-t border-white/10 text-[10px] text-gray-400">
                        <p>© 2026 نظام تور العقاري. جميع الحقوق محفوظة.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};
